import { LicenseManager } from '@ag-grid-enterprise/core';
import { AgGridReact } from '@ag-grid-community/react';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import { observer } from 'mobx-react';
import styled from 'styled-components';

import { Flex } from '../Flex';
import ImageRenderer from '../ImageRenderer';

LicenseManager.setLicenseKey(process.env.NEXT_PUBLIC_AG_GRID_KEY);

/**
 *
 * @param root0
 * @param root0.groupByKey
 * @param root0.headerButtonsPadding
 * @param root0.onGridReady
 * @param root0.options
 * @param root0.tableHeaderComponent
 */
function Table({
  groupByKey,
  headerButtonsPadding = 4,
  onGridReady,
  options,
  tableHeaderComponent,
  ...restGridProps
}) {
  const defaultOptions = {
    animateRows: true,
    cacheBlockSize: 100,
    defaultColDef: {
      filter: true,
      maxWidth: 200,
      menuTabs: [],
      minWidth: 125,
      resizable: true,
      sortable: true,
      suppressMenu: true,
    },
    getServerSideGroupKey: (dataItem) => dataItem[groupByKey],
    isRowSelectable: (rowNode) => rowNode.level === 0,
    isServerSideGroup: (dataItem) => dataItem?.variants?.length > 1,
    pagination: true,
    paginationPageSize: 25,
    purgeClosedRowNodes: true,
    rowBuffer: 1000,
    rowModelType: 'serverSide',
    rowSelection: 'multiple',
    rowStyle: { cursor: 'pointer' },
    suppressMaxRenderedRowRestriction: true,
    ...options,
  };

  const frameworkComponents = { imageRenderer: ImageRenderer };

  const handleOnGridReady = (params) => {
    autoSizeColumns(params);
    if (onGridReady) {
      onGridReady(params);
    }
  };

  const autoSizeColumns = (params) => {
    params.api?.sizeColumnsToFit();
  };

  return (
    <TableContainer className="ag-theme-material" data-testid="table-container">
      {tableHeaderComponent && (
        <TableHeaderContainer ml={headerButtonsPadding}>
          {tableHeaderComponent}
        </TableHeaderContainer>
      )}
      <AgGridReact
        frameworkComponents={frameworkComponents}
        gridOptions={defaultOptions}
        modules={AllModules}
        onGridReady={handleOnGridReady}
        onNewColumnsLoaded={autoSizeColumns}
        {...restGridProps}
      ></AgGridReact>
    </TableContainer>
  );
}

const TableContainer = styled.div`
  height: 600px;
  font-size: ${({ theme }) => theme.fontSizes[0]}px !important;

  .ag-header {
    background-color: ${({ theme }) => theme.colors.neutrals[2]};
  }

  .ag-row {
    font-size: ${({ theme }) => theme.fontSizes[0]}px !important;
  }

  .ag-header-cell {
    &.ag-column-hover {
      background-color: ${({ theme }) => theme.colors.neutrals[2]} !important;
    }
  }

  .ag-header-cell-sortable {
    &.ag-column-hover {
      background-color: ${({ theme }) => theme.colors.neutrals[3]} !important;
    }
  }

  .ag-header-cell-text {
    text-transform: uppercase;
  }

  .group-button {
    margin-left: 5px;
  }

  .ag-checkbox-input {
    cursor: pointer;
  }
`;

const TableHeaderContainer = styled(Flex).attrs({
  my: 2,
})`
  height: ${({ theme }) => theme.sizes[3]}px;
  border-radius: 2px;
`;

export default observer(Table);
